import type { AxiosError } from "axios";
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkButtonMediumV2 } from "../../../../../../components/Buttons/Buttons";
import {
  LoadingIcon,
  ResyncIcon,
} from "../../../../../../components/Icons/Icons";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import type { BarentzIntegrationMasterDataExportBatches } from "../../../../../../types/types.PIM";
import { endpoints } from "../../../../../../endpoints";

export interface APIResyncButtonProps {
  barentzApiBatches: BarentzIntegrationMasterDataExportBatches[];
  storefrontId: string;
  onResyncComplete: () => void | Promise<unknown>;
  enabled: boolean;
}

export const APIResyncButton = ({
  barentzApiBatches,
  storefrontId,
  onResyncComplete,
  enabled,
}: APIResyncButtonProps) => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();
  const [isResyncing, setIsResyncing] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const shouldShow =
      enabled && barentzApiBatches.some((batch) => batch.status === "failed");
    setShowButton(shouldShow);
  }, [enabled, barentzApiBatches]);

  const handleResync = async () => {
    const failedBatches = barentzApiBatches.filter(
      (batch) => batch.status === "failed"
    );
    const product_numbers = [
      ...new Set(failedBatches.map((b) => b.product_number)),
    ];
    const sku_numbers = [...new Set(failedBatches.map((b) => b.sku_number))];
    if (!product_numbers.length || !sku_numbers.length) return;

    setIsResyncing(true);
    try {
      await axios.post(
        endpoints.v2_storefronts_id_integrations_barentz_api_resync(
          storefrontId
        ),
        { product_numbers, sku_numbers }
      );
      await onResyncComplete();
      notifySuccess(t("Re-sync completed successfully"));
    } catch (error) {
      notifyError(
        (error as AxiosError)?.response?.data?.message ??
          t("Re-sync failed. Please try again.")
      );
    } finally {
      setIsResyncing(false);
    }
  };

  if (!showButton) {
    return null;
  }

  return (
    <LinkButtonMediumV2 onClick={handleResync} disabled={isResyncing}>
      {isResyncing ? (
        <LoadingIcon width={20} height={20} />
      ) : (
        <ResyncIcon fill="currentColor" />
      )}
      {t("Re-sync")}
    </LinkButtonMediumV2>
  );
};
